import {gql, useMutation} from '@apollo/client';
import {Pda} from './components/Pda';
import {Header} from './components/Header';
import {useState} from 'react';
import {Column, createRecord, insertRecordBySeq, ResultTable, ScanRecord} from './components/ResultTable';
import {ScanInput} from './components/ScanInput';
import {isValidBarCode, showBarCodeError, isPpmod99MBagNumber} from 'utils/BarCodeUtil';
import {parseException, showError, showRepeatedScanError, parseExceptionMessageKey} from 'utils/exceptionUtils';
import {playScanFailedAoSound} from 'utils/scanUtils';
import dayjs from "dayjs";
import {t} from '../../i18n';
import { formatLocalizedTime } from '../../utils/dateFormatUtils';
import { useScanQueueConsumer } from "./components/useScanQueueConsumer";

const HANDLE_WHS_DEPARTURE_SCAN_EVENT = gql`
mutation HandleWhsDepartureScanEvent($barCode: String!) {
  success: handleWhsDepartureScanEvent(barCode: $barCode)
}
`;

interface HandleScanResult {
    success: boolean;
}

const PageBody = () => {

    const [successRecords, setSuccessRecords] = useState<ScanRecord[]>([]);
    const [failedRecords, setFailedRecords] = useState<ScanRecord[]>([]);
    const [scanValue, setScanValue] = useState('');

    const [handleWhsDepartureScanEvent] = useMutation<HandleScanResult>(HANDLE_WHS_DEPARTURE_SCAN_EVENT);

    const onProcess = async (event: { id: string; seq: number; code: string; ts: number }) => {
        const barCode = event.code;
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        setScanValue(barCode);

        try {
            const { data } = await handleWhsDepartureScanEvent({variables: {barCode}});
            const success = data?.success || false;

            if (success) {
                setSuccessRecords(prev => insertRecordBySeq(prev, createRecord({
                    value2: barCode,
                    time: formatLocalizedTime(),
                    seq: event.seq
                })));
            } else {
                const message = t('pda.common.scanFailed');
                playScanFailedAoSound();
                showError({title: t('pda.common.scanFailed'), message});
                setFailedRecords(prev => insertRecordBySeq(prev, createRecord({
                    value2: barCode,
                    failedReason: message,
                    time: formatLocalizedTime(),
                    seq: event.seq
                })));
            }
        } catch (error) {
            const messageKey = parseExceptionMessageKey(error);
            let message: string;
            if (messageKey === "errors.business.alreadyScanned") {
                showRepeatedScanError(error);
                const { message: errorMessage } = parseException(error);
                message = errorMessage;
            } else {
                const {title, message: errorMessage} = parseException(error);
                playScanFailedAoSound();
                showError({title, message: errorMessage});
                message = errorMessage;
            }
            setFailedRecords(prev => insertRecordBySeq(prev, createRecord({
                value2: barCode,
                failedReason: message,
                time: formatLocalizedTime(),
                seq: event.seq
            })));
        }
    };

    const { enqueueScan } = useScanQueueConsumer({ maxConcurrency: 1, onProcess });

    const handleScan = (code: string) => {
        // Temu PPMOD 大箱条码（99M...）不符合 BAG/TT/SWX 严格格式，但业务侧视作包牌；
        // 与后端 BarCodeUtil.isPpmod99MBagNumber 路由口径一致，前端显式放行避免依赖 isTrackingNumber 兜底
        if (isPpmod99MBagNumber(code) || isValidBarCode(code)) {
            enqueueScan(code);
        } else {
            showBarCodeError(code);
        }
    };

    const successColumns: Column[] = [
        { title: t('pda.common.bagPackage'), key: 'value2' },
        { title: t('pda.common.time'), key: 'time' }
    ];

    const failedColumns: Column[] = [
        ...successColumns,
        { title: t('pda.common.failedReason'), key: 'failedReason' }
    ];

    return (
        <div className="flex flex-col gap-2">
            <ScanInput
                placeholder={t('pda.pages.departure.bagCodeTrackingNo')}
                value={scanValue}
                onScan={handleScan}
            />
            <ResultTable
                successRecords={successRecords}
                failedRecords={failedRecords}
                successColumns={successColumns}
                failedColumns={failedColumns}
            />
        </div>
    );
}

export default function DeparturePage() {
    return (
        <div className="w-full h-full flex flex-col gap-2 p-2">
            <Header title={t('pda.pages.departure.departureScan')} />
            <Pda/>
            <PageBody/>
        </div>
    );
}
